// src/components/admin/payroll/components/AttendanceValidationModal.js
// 3-step attendance validation wizard for payroll processing

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    X, ChevronLeft, ChevronRight, Clock, Calendar, Users, CheckCircle,
    AlertCircle, User, Eye, Edit2, Save, RotateCcw
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';

const AttendanceValidationModal = ({
    isOpen,
    onClose,
    employees = [],
    workingDaysData = {},
    period,
    onComplete,
    loading = false
}) => {
    const { theme } = useTheme();

    // Wizard State
    const [currentStep, setCurrentStep] = useState(1);
    const [partialDayDecisions, setPartialDayDecisions] = useState({});
    const [lopOverrides, setLOPOverrides] = useState({});
    const [prorationDecisions, setProrationDecisions] = useState({});

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
        }
    }, [isOpen]);

    // Categorize employees
    const { partialDayEmployees, lopEmployees, proratedEmployees, summary } = useMemo(() => {
        const partial = [];
        const lop = [];
        const prorated = [];

        employees.forEach(emp => {
            // Check for partial days
            const partialDays = emp.partial_days || emp.partialDays || [];
            if (partialDays.length > 0) {
                partial.push({
                    ...emp,
                    partialDays
                });
            }

            // Check for LOP
            const lopDays = emp.lop_days || emp.lopDays || 0;
            if (lopDays > 0) {
                lop.push({
                    ...emp,
                    lopDays,
                    lopDeduction: emp.lop_deduction || emp.lopDeduction || 0
                });
            }

            // Check for pro-ration (joiners/exits)
            const isProrated = emp.isProrated || emp.is_prorated;
            if (isProrated) {
                prorated.push({
                    ...emp,
                    joinerType: emp.joinerType || emp.joiner_type,
                    effectiveDays: emp.effectiveDays || emp.effective_days || 0
                });
            }
        });

        return {
            partialDayEmployees: partial,
            lopEmployees: lop,
            proratedEmployees: prorated,
            summary: {
                totalEmployees: employees.length,
                partialDayCount: partial.length,
                lopCount: lop.length,
                proratedCount: prorated.length,
                totalLOPDays: lop.reduce((sum, e) => sum + (e.lopDays || 0), 0)
            }
        };
    }, [employees]);

    // Partial Day Options
    const partialDayOptions = [
        { value: 'full_day', label: 'Count as Full Day', factor: 1.0 },
        { value: 'half_day', label: 'Count as Half Day', factor: 0.5 },
        { value: 'hourly', label: 'Calculate by Hours', factor: null }
    ];

    // Handle partial day decision
    const handlePartialDayDecision = (employeeId, dayIndex, decision) => {
        setPartialDayDecisions(prev => ({
            ...prev,
            [`${employeeId}_${dayIndex}`]: decision
        }));
    };

    // Apply bulk partial day decision
    const applyBulkPartialDecision = (decision) => {
        const newDecisions = {};
        partialDayEmployees.forEach(emp => {
            emp.partialDays.forEach((day, idx) => {
                newDecisions[`${emp.id}_${idx}`] = decision;
            });
        });
        setPartialDayDecisions(newDecisions);
    };

    // Handle LOP override
    const handleLOPOverride = (employeeId, newLOPDays, reason) => {
        setLOPOverrides(prev => ({
            ...prev,
            [employeeId]: { lopDays: newLOPDays, reason }
        }));
    };

    // Handle proration decision
    const handleProrationDecision = (employeeId, decision) => {
        setProrationDecisions(prev => ({
            ...prev,
            [employeeId]: decision
        }));
    };

    // Apply bulk proration decision
    const applyBulkProrationDecision = (decision) => {
        const newDecisions = {};
        proratedEmployees.forEach(emp => {
            newDecisions[emp.id] = decision;
        });
        setProrationDecisions(newDecisions);
    };

    // Handle step navigation
    const goToNextStep = () => {
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const goToPrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Handle completion
    const handleComplete = () => {
        onComplete?.({
            partialDayDecisions,
            lopOverrides,
            prorationDecisions
        });
    };

    // Step indicator
    const StepIndicator = () => (
        <div className="flex items-center justify-center gap-4 mb-6">
            {[1, 2, 3].map(step => (
                <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                        currentStep === step
                            ? 'bg-blue-600 text-white'
                            : currentStep > step
                                ? theme === 'dark'
                                    ? 'bg-green-900/50 text-green-400 border border-green-700'
                                    : 'bg-green-100 text-green-700 border border-green-300'
                                : theme === 'dark'
                                    ? 'bg-gray-700 text-gray-400'
                                    : 'bg-gray-200 text-gray-500'
                    }`}>
                        {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step}
                    </div>
                    {step < 3 && (
                        <div className={`w-12 h-0.5 ml-2 ${
                            currentStep > step
                                ? 'bg-green-500'
                                : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`} />
                    )}
                </div>
            ))}
        </div>
    );

    // Step 1: Partial Attendance Review
    const renderStep1 = () => (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Step 1: Partial Attendance Review
                </h3>
                {partialDayEmployees.length > 0 && (
                    <div className="flex gap-2">
                        <button
                            onClick={() => applyBulkPartialDecision('full_day')}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            Mark All as Full Day
                        </button>
                        <button
                            onClick={() => applyBulkPartialDecision('half_day')}
                            className={`px-3 py-1.5 text-sm rounded-lg ${
                                theme === 'dark'
                                    ? 'bg-yellow-900/50 text-yellow-400 hover:bg-yellow-900/70'
                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                        >
                            Mark All as Half Day
                        </button>
                    </div>
                )}
            </div>

            {partialDayEmployees.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                }`}>
                    <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-500'
                    }`} />
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        No Partial Days Found
                    </p>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        All employees have complete attendance records
                    </p>
                </div>
            ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {partialDayEmployees.map(emp => (
                        <div key={emp.id} className={`p-4 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                        }`}>
                            <div className="flex items-center gap-3 mb-3">
                                <User className={`w-5 h-5 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`} />
                                <span className={`font-medium ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {emp.first_name} {emp.last_name}
                                </span>
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                    ({emp.employee_code})
                                </span>
                                <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
                                    theme === 'dark'
                                        ? 'bg-orange-900/30 text-orange-400'
                                        : 'bg-orange-100 text-orange-700'
                                }`}>
                                    {emp.partialDays.length} partial day(s)
                                </span>
                            </div>

                            <div className="space-y-2">
                                {emp.partialDays.map((day, idx) => (
                                    <div key={idx} className={`flex items-center justify-between p-3 rounded ${
                                        theme === 'dark' ? 'bg-gray-700/50' : 'bg-white'
                                    }`}>
                                        <div>
                                            <p className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                <Calendar className="w-4 h-4 inline mr-1" />
                                                {new Date(day.date).toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </p>
                                            <p className={`text-xs ${
                                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                            }`}>
                                                <Clock className="w-3 h-3 inline mr-1" />
                                                {day.hours || day.work_hours || 0} hours worked (out of 8)
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            {partialDayOptions.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => handlePartialDayDecision(emp.id, idx, opt.value)}
                                                    className={`px-3 py-1 text-xs rounded ${
                                                        partialDayDecisions[`${emp.id}_${idx}`] === opt.value
                                                            ? 'bg-blue-600 text-white'
                                                            : theme === 'dark'
                                                                ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Step 2: LOP & Attendance Review
    const renderStep2 = () => (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Step 2: LOP & Attendance Review
                </h3>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {lopEmployees.length} employees with LOP
                </div>
            </div>

            {/* Working Days Summary */}
            <div className={`grid grid-cols-4 gap-4 mb-4 p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
            }`}>
                <div className="text-center">
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {workingDaysData.totalDays || 30}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Days
                    </p>
                </div>
                <div className="text-center">
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {workingDaysData.workingDays || 22}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Working Days
                    </p>
                </div>
                <div className="text-center">
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                        {workingDaysData.holidayCount || 2}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Holidays
                    </p>
                </div>
                <div className="text-center">
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`}>
                        {workingDaysData.weeklyOffCount || 4}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Weekly Offs
                    </p>
                </div>
            </div>

            {lopEmployees.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                }`}>
                    <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-500'
                    }`} />
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        No LOP Found
                    </p>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        All employees have full attendance
                    </p>
                </div>
            ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                    {lopEmployees.map(emp => {
                        const override = lopOverrides[emp.id];
                        const effectiveLOP = override?.lopDays ?? emp.lopDays;

                        return (
                            <div key={emp.id} className={`p-4 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <User className={`w-5 h-5 ${
                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`} />
                                        <div>
                                            <span className={`font-medium ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {emp.first_name} {emp.last_name}
                                            </span>
                                            <div className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                Present: {emp.present_days || 0} | Leave: {emp.approved_leaves || 0}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                }`}>
                                                    LOP Days:
                                                </span>
                                                <input
                                                    type="number"
                                                    value={effectiveLOP}
                                                    onChange={(e) => handleLOPOverride(
                                                        emp.id,
                                                        parseInt(e.target.value) || 0,
                                                        'Manual override'
                                                    )}
                                                    className={`w-16 px-2 py-1 text-center rounded ${
                                                        theme === 'dark'
                                                            ? 'bg-gray-700 border-gray-600 text-white'
                                                            : 'bg-white border-gray-300 text-gray-900'
                                                    } border`}
                                                    min={0}
                                                    max={workingDaysData.workingDays || 22}
                                                />
                                                {override && (
                                                    <button
                                                        onClick={() => {
                                                            const { [emp.id]: removed, ...rest } = lopOverrides;
                                                            setLOPOverrides(rest);
                                                        }}
                                                        className={`p-1 rounded ${
                                                            theme === 'dark'
                                                                ? 'text-gray-400 hover:bg-gray-700'
                                                                : 'text-gray-500 hover:bg-gray-200'
                                                        }`}
                                                        title="Reset to original"
                                                    >
                                                        <RotateCcw className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            <p className={`text-xs mt-1 ${
                                                theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                            }`}>
                                                Deduction: ₹{emp.lopDeduction?.toLocaleString() || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    // Step 3: Summary & Confirm
    const renderStep3 = () => (
        <div>
            <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Step 3: Summary & Confirm
            </h3>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-lg text-center ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                }`}>
                    <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {summary.totalEmployees}
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Employees
                    </p>
                </div>
                <div className={`p-4 rounded-lg text-center ${
                    theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50'
                }`}>
                    <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`}>
                        {summary.partialDayCount}
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Partial Days Resolved
                    </p>
                </div>
                <div className={`p-4 rounded-lg text-center ${
                    theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50'
                }`}>
                    <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                        {summary.lopCount}
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        LOP Applied ({summary.totalLOPDays} days)
                    </p>
                </div>
                <div className={`p-4 rounded-lg text-center ${
                    theme === 'dark' ? 'bg-purple-900/20' : 'bg-purple-50'
                }`}>
                    <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`}>
                        {summary.proratedCount}
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Joiners/Exits
                    </p>
                </div>
            </div>

            {/* Joiners/Exits Section */}
            {proratedEmployees.length > 0 && (
                <div className={`mb-6 p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                }`}>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            New Joiners & Exits - Pro-ration Decision
                        </h4>
                        <div className="flex gap-2">
                            <button
                                onClick={() => applyBulkProrationDecision('prorated')}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Pro-rate All
                            </button>
                            <button
                                onClick={() => applyBulkProrationDecision('full_month')}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Full Month All
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {proratedEmployees.map(emp => (
                            <div key={emp.id} className={`flex items-center justify-between p-3 rounded ${
                                theme === 'dark' ? 'bg-gray-700/50' : 'bg-white'
                            }`}>
                                <div className="flex items-center gap-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        emp.joinerType === 'joiner'
                                            ? theme === 'dark' ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                                            : emp.joinerType === 'exit'
                                                ? theme === 'dark' ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                                                : theme === 'dark' ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-700'
                                    }`}>
                                        {emp.joinerType === 'joiner' ? '📥 Joiner' :
                                            emp.joinerType === 'exit' ? '📤 Exit' : '📥📤 Join+Exit'}
                                    </span>
                                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                                        {emp.first_name} {emp.last_name}
                                    </span>
                                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        ({emp.effectiveDays} / {workingDaysData.workingDays || 22} days)
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleProrationDecision(emp.id, 'prorated')}
                                        className={`px-3 py-1 text-xs rounded ${
                                            prorationDecisions[emp.id] === 'prorated'
                                                ? 'bg-blue-600 text-white'
                                                : theme === 'dark'
                                                    ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        Pro-rated
                                    </button>
                                    <button
                                        onClick={() => handleProrationDecision(emp.id, 'full_month')}
                                        className={`px-3 py-1 text-xs rounded ${
                                            prorationDecisions[emp.id] === 'full_month'
                                                ? 'bg-blue-600 text-white'
                                                : theme === 'dark'
                                                    ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        Full Month
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Confirmation Message */}
            <div className={`p-4 rounded-lg ${
                theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
            }`}>
                <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                        <p className={`font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-700'}`}>
                            Ready to Apply
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            All attendance decisions will be applied to the payroll calculation.
                            You can still make changes in the employee review section after this step.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl shadow-xl ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div>
                        <h2 className={`text-xl font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Attendance Validation Wizard
                        </h2>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {period}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-500'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                    <StepIndicator />

                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                </div>

                {/* Footer */}
                <div className={`flex items-center justify-between px-6 py-4 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <button
                        onClick={goToPrevStep}
                        disabled={currentStep === 1}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                            currentStep === 1
                                ? 'opacity-50 cursor-not-allowed'
                                : theme === 'dark'
                                    ? 'hover:bg-gray-700 text-gray-300'
                                    : 'hover:bg-gray-100 text-gray-700'
                        }`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>

                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Step {currentStep} of 3
                    </div>

                    {currentStep < 3 ? (
                        <button
                            onClick={goToNextStep}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleComplete}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                        >
                            <CheckCircle className="w-4 h-4" />
                            Confirm & Continue
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendanceValidationModal;
